/**
 * Honest-scarcity "in N carts" signal.
 *
 * Talks to the backend's App Proxy endpoints (/apps/box/signal/*), which are
 * DARK until a feature flag flips server-side — until then every call here
 * 404s. That is expected, not an error: every fetch below fails silently
 * (no console output, no thrown exception that could interrupt page JS),
 * so shipping this file today is a no-op in production and only turns on
 * the moment the backend does.
 *
 * Two responsibilities:
 *  1. Tell the backend which variants this shopper currently holds, so other
 *     shoppers' counts exclude them (POST /apps/box/signal/cart).
 *  2. Show shoppers how many OTHER carts hold the same variant, on the
 *     product page and in their own cart (GET /apps/box/signal/counts).
 *
 * constants.js and pubsub.js are loaded with `defer` in layout/theme.liquid
 * and this script is also deferred and appears later in the document, so
 * subscribe/PUB_SUB_EVENTS are expected to exist — guard anyway in case
 * load order ever changes (same pattern as tt-quick-add-feedback.js and
 * tt-sticky-checkout.js).
 */
(function () {
  var SIGNAL_CART_URL = '/apps/box/signal/cart';
  var SIGNAL_COUNTS_URL = '/apps/box/signal/counts';

  // Session state, populated once /cart.js resolves. Nothing below runs
  // ahead of that — every entry point checks cartToken first.
  var cartToken = null;
  var cartVariantIds = [];

  // Tracks whether this page-life has ever told the backend about a
  // non-empty cart, so we know whether an empty cart is new information
  // (send once, to clear holds) or just the steady state (skip — no point
  // spamming an empty-cart ping every time an unrelated cartUpdate fires,
  // e.g. a quantity edit on a different line that nets out to no change).
  var hadHeldVariants = false;

  // Set by initCartPage() when the cart-page container exists, so
  // handleCartUpdate can ask it to re-fetch counts for the current line
  // items. Left null everywhere else (PDP has nothing to refresh here —
  // see the comment in initPDPBadge).
  var cartPageRefresh = null;

  /**
   * Both mounts (the PDP span and the cart-line badges) share one class, so
   * one small stylesheet covers both rather than duplicating rules across
   * two Liquid files. Injected once, guarded by id so a second script
   * instance (there shouldn't be one — see the note in
   * sections/main-cart-items.liquid) can't double it up.
   */
  function injectStyles() {
    if (document.getElementById('tt-signal-badge-style')) return;
    var style = document.createElement('style');
    style.id = 'tt-signal-badge-style';
    style.textContent =
      '.tt-signal-badge{font-size:1.3rem;color:var(--tt-green-deep,#0c6236);' +
      "font-family:var(--tt-body);}" +
      /* PDP: a soft-green sibling of the free-shipping chip — identical
         construction (flex/gap/width/type/padding/margin all copied from
         tt-design-system's .product__text:has(strong)), only the tint
         differs: the house green at the 0.08 alpha the menu chips use. */
      '.tt-signal-badge--pdp{display:flex;align-items:center;gap:1rem;' +
      'width:100%;background:rgba(21,128,74,0.08);color:var(--tt-pine);' +
      'font-size:1.4rem;font-weight:700;letter-spacing:0.01em;' +
      'border-radius:0;padding:0.7rem 1.4rem;margin:0 0 var(--tt-sp-2);}' +
      /* display:flex above beats the hidden attribute's UA display:none,
         which left a lone eye on pages with no count — restate it */
      '.tt-signal-badge--pdp[hidden]{display:none;}' +
      '.tt-signal-badge__eye{flex-shrink:0;display:block;width:2rem;height:2rem;}' +
      '.tt-signal-badge--cart{display:block;margin-top:0.4rem;}';
    document.head.appendChild(style);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function variantIdsFromItems(items) {
    if (!items) return [];
    var ids = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].variant_id) ids.push(items[i].variant_id);
    }
    return ids;
  }

  /**
   * Resolves the cart's current variant ids for a cartUpdate event. Prefers
   * the event's own cart payload when it already carries the full item
   * list (cart-items' quantity-change events do), which avoids a redundant
   * fetch; falls back to re-fetching /cart.js otherwise (product-form's
   * add-to-cart event only carries the single added line, not the cart).
   * Resolves null — never an empty array — when the cart can't be read, so
   * the caller can tell "cart is empty" apart from "request failed" and
   * skip the ping rather than wrongly clearing real holds.
   */
  function resolveVariantIdsForEvent(event) {
    // Only trust the payload as the FULL cart when it carries a token:
    // /cart/change.js responses (cart-page quantity edits) are the whole
    // cart object, token included, but /cart/add.js responses (product-form
    // adds) are just {items: [the added line]} with no token — treating
    // those as the full cart would replace every other hold this shopper
    // has. No token → fall through to a fresh /cart.js fetch.
    if (
      event &&
      event.cartData &&
      typeof event.cartData.token === 'string' &&
      Object.prototype.toString.call(event.cartData.items) === '[object Array]'
    ) {
      cartToken = event.cartData.token;
      return Promise.resolve(variantIdsFromItems(event.cartData.items));
    }
    return fetchCart().then(function (cart) {
      if (!cart) return null;
      if (cart.token) cartToken = cart.token;
      return variantIdsFromItems(cart.items);
    });
  }

  /**
   * Replaces this shopper's holds with the given variant list. Silently
   * skipped for an empty list unless we previously told the backend about
   * held variants — that one extra empty ping is what clears those holds;
   * without it, a shopper who empties their cart would keep "reserving"
   * stock in other shoppers' counts until their session expires.
   */
  function pingCart(variantIds) {
    if (!cartToken) return;
    var ids = variantIds || [];
    if (ids.length === 0 && !hadHeldVariants) return;
    hadHeldVariants = ids.length > 0;

    fetch(SIGNAL_CART_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cart_token: cartToken, variant_ids: ids }),
    }).catch(function () {});
  }

  /**
   * GET /apps/box/signal/counts for a set of variant ids. Tokens contain
   * `?` and `=`, so params are always built via URLSearchParams rather than
   * string-concatenated. Resolves null (never throws) on any failure —
   * 404 (flag still dark), 403, 429, or a network error all look the same
   * from here: no badge shown.
   */
  function fetchCounts(variantIds) {
    if (!cartToken || !variantIds || variantIds.length === 0) return Promise.resolve(null);
    var params = new URLSearchParams();
    params.set('product_variants', variantIds.join(','));
    params.set('cart_token', cartToken);
    return fetch(SIGNAL_COUNTS_URL + '?' + params.toString())
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Word order differs between the two surfaces ("right now" trails the
  // noun on the PDP, "other" leads it in the cart) so these are two
  // dedicated builders rather than one parameterized template.
  function pdpLabel(n) {
    var noun = n === 1 ? 'cart' : 'carts';
    return 'In ' + String(n) + ' ' + noun + ' right now';
  }

  function cartLineLabel(n) {
    var noun = n === 1 ? 'cart' : 'carts';
    return 'Also in ' + String(n) + ' other ' + noun;
  }

  /**
   * PDP badge: mount carries the product's own variant ids (set server-side
   * in sections/main-product.liquid, only when product.available — a
   * sold-out listing must never claim someone else is holding it). Hidden
   * by default in markup; only unhidden here once a real count comes back,
   * so "flag still dark" and "nobody else holds this" render identically —
   * nothing.
   *
   * No cartUpdate re-fetch here: the count is of OTHER carts, and this
   * shopper's own add/remove on this product can't change that number.
   */
  function initPDPBadge() {
    var mount = document.querySelector('[data-tt-signal-variants]');
    if (!mount) return;

    var variantIds = (mount.getAttribute('data-tt-signal-variants') || '')
      .split(',')
      .map(function (id) {
        return id.trim();
      })
      .filter(Boolean);
    if (variantIds.length === 0) return;

    fetchCounts(variantIds)
      .then(function (data) {
        if (!data || !data.counts) return;
        var max = 0;
        for (var key in data.counts) {
          if (!Object.prototype.hasOwnProperty.call(data.counts, key)) continue;
          var n = data.counts[key];
          if (typeof n === 'number' && n > max) max = n;
        }
        if (max >= 1) {
          // text goes into the inner span so the eye icon markup survives
          var textEl = mount.querySelector('[data-tt-signal-text]');
          if (textEl) {
            textEl.textContent = pdpLabel(max);
          } else {
            mount.textContent = pdpLabel(max);
          }
          mount.hidden = false;
        }
      })
      .catch(function () {});
  }

  /**
   * Cart-page badges: one small line under each held item's product name,
   * "also in N other carts". No Liquid mount for these — rows already
   * carry the variant id we need (the quantity input's
   * data-quantity-variant-id, per sections/main-cart-items.liquid), so
   * badges are inserted purely by JS.
   *
   * cart.js replaces #main-cart-items' innerHTML asynchronously *after* the
   * cartUpdate event fires (quantity edits, line removal), which wipes any
   * badge already inserted. A MutationObserver on the container catches
   * that swap and re-applies from the cached counts — debounced, since a
   * single section swap can fire several mutation records — instead of
   * re-fetching on every DOM tick. applyBadges() itself is idempotent
   * (checks for an existing badge before inserting), so it's safe to call
   * from both the observer and the post-fetch path without risking a
   * double-insert if they land close together.
   */
  function initCartPage() {
    var container = document.getElementById('main-cart-items');
    if (!container) return;

    var countsCache = {};

    function applyBadges() {
      var rows = container.querySelectorAll('tr.cart-item');
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var qtyInput = row.querySelector('[data-quantity-variant-id]');
        if (!qtyInput) continue;
        var variantId = qtyInput.getAttribute('data-quantity-variant-id');
        if (!variantId) continue;

        var nameEl = row.querySelector('.cart-item__name');
        if (!nameEl) continue;

        // Idempotent: a re-render or a second observer tick must never
        // stack a second badge under the same name.
        if (nameEl.parentNode.querySelector('.tt-signal-badge')) continue;

        var n = countsCache[variantId];
        if (typeof n !== 'number' || n < 1) continue;

        var badge = document.createElement('div');
        badge.className = 'tt-signal-badge tt-signal-badge--cart';
        badge.textContent = cartLineLabel(n);
        nameEl.insertAdjacentElement('afterend', badge);
      }
    }

    function refreshCounts() {
      var inputs = container.querySelectorAll('[data-quantity-variant-id]');
      var variantIds = [];
      for (var i = 0; i < inputs.length; i++) {
        var id = inputs[i].getAttribute('data-quantity-variant-id');
        if (id) variantIds.push(id);
      }
      if (variantIds.length === 0) return;

      fetchCounts(variantIds)
        .then(function (data) {
          if (!data || !data.counts) return;
          countsCache = data.counts;
          applyBadges();
        })
        .catch(function () {});
    }

    if ('MutationObserver' in window) {
      var debounceTimer = null;
      var observer = new MutationObserver(function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(applyBadges, 100);
      });
      observer.observe(container, { childList: true, subtree: true });
    }
    // No IntersectionObserver-style feature-detect fallback needed here:
    // without MutationObserver support the first applyBadges() pass below
    // still runs, it just won't survive the next section re-render — an
    // acceptable degrade for a cosmetic badge, same call as
    // tt-sticky-checkout.js makes for IntersectionObserver.

    refreshCounts();
    cartPageRefresh = refreshCounts;
  }

  function handleCartUpdate(event) {
    try {
      resolveVariantIdsForEvent(event).then(function (variantIds) {
        // null means the cart couldn't be read this cycle (fetch failed) —
        // skip silently rather than pinging a guessed state.
        if (variantIds === null) return;
        cartVariantIds = variantIds;
        pingCart(cartVariantIds);
        if (cartPageRefresh) cartPageRefresh();
      });
    } catch (e) {
      // Never let a signal-badge failure touch the real cart update flow.
    }
  }

  function init() {
    fetchCart().then(function (cart) {
      // No token at all means /cart.js itself failed — do nothing further;
      // every call below needs a token, so there is nothing safe to do.
      if (!cart || !cart.token) return;

      injectStyles();
      cartToken = cart.token;
      cartVariantIds = variantIdsFromItems(cart.items);

      pingCart(cartVariantIds);
      initPDPBadge();
      initCartPage();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
      }
    });
  }

  try {
    init();
  } catch (e) {
    // Feature ships dark until the backend flag flips; nothing here should
    // ever be able to break the storefront around it.
  }
})();
